/* This example requires Tailwind CSS v2.0+ */

import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import classNames from "classnames";
import { Fragment, ReactNode, useMemo } from "react";

export interface SelectOption {
  id: string | number | null;
  name: string;
  description?: ReactNode;
  label?: string;
  value?: string | number | null;
}

export interface SelectProps<T extends SelectOption> {
  value: string | number | null;
  options: T[];
  label?: ReactNode;
  placeholder?: ReactNode;
  initialValue?: T;
  onChange: (val: any) => void;
  valueKey?: keyof T;
  labelKey?: keyof T;
  error?: string;
  hint?: string;
  disabled?: boolean;
  description?: ReactNode;
}

export interface SelectOptionProps<T> {
  option: T;
}

function SelectOption<T extends SelectOption>({ option }: SelectOptionProps<T>) {
  return (
    <Listbox.Option
      key={option.id}
      className={({ active }) =>
        classNames(
          {
            "text-white bg-purple-600": active,
            "text-foreground": !active,
          },
          "cursor-default select-none relative py-2 pl-3 pr-9",
        )
      }
      value={option.id}
    >
      {({ selected, active }) => (
        <>
          <span
            className={classNames(selected ? "font-semibold" : "font-normal", "block truncate")}
          >
            {option.name}
          </span>

          {selected ? (
            <span
              className={classNames(
                active ? "text-white" : "text-purple-600",
                "absolute inset-y-0 right-0 flex items-center pr-4",
              )}
            >
              <CheckIcon className="w-5 h-5" aria-hidden="true" />
            </span>
          ) : null}
        </>
      )}
    </Listbox.Option>
  );
}

function Select<T extends SelectOption>({
  value,
  onChange,
  options,
  label,
  placeholder = "Any",
  labelKey = "name",
  error,
  hint,
}: SelectProps<T>) {
  const activeOption: any = useMemo(
    () => options.find((option) => option.id === value) || null,
    [options, value],
  );
  return (
    <Listbox
      value={value}
      onChange={(nextVal) => {
        onChange(nextVal);
      }}
    >
      {({ open }) => (
        <div data-cy={`listbox:${label}`}>
          <div
            className={classNames("flex  items-end", {
              "justify-between mb-1": !!label,
              "justify-end": !label,
            })}
          >
            {label && (
              <Listbox.Label className="block text-sm font-medium text-foreground">
                {label}
              </Listbox.Label>
            )}
            {error ? (
              <p className="text-xs text-red-600 dark:text-red-500">{error}</p>
            ) : hint ? (
              <span className="block text-xs text-muted-foreground">{hint}</span>
            ) : null}
          </div>
          <div className={classNames("relative")}>
            <Listbox.Button className="relative w-full py-2 pl-3 pr-10 text-left bg-background border border-border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm">
              <span className="block h-5 truncate">
                {activeOption ? activeOption[labelKey] : placeholder}
              </span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <SelectorIcon className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                className={classNames(
                  "absolute right-0 z-10 w-full py-1 mt-1 overflow-auto text-base origin-top-right bg-background border border-border rounded-md shadow-lg min-w-max max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm",
                )}
              >
                {options.map((option) => (
                  <SelectOption key={option.id} option={option} />
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </div>
      )}
    </Listbox>
  );
}

Select.Option = SelectOption;

export default Select;
